import { useAppSettingsStore } from '@/shared/lib';
import { useSessionStore } from '@/entities/session';
import { useEffect } from 'react';
import i18n from '../i18n';
import type { Language } from '@/shared/types';
import { FALLBACK_LANGUAGE, SUPPORTED_LANGUAGES } from '@/shared/config';

const LanguageProvider = () => {
  const { language, setLanguage } = useAppSettingsStore();

  const applyLanguage = (lng: Language) => {
    if (i18n.language !== lng) {
      i18n.changeLanguage(lng);
    }
  };

  const getLanguageFromPath = (): Language | null => {
    const path = window.location.pathname;
    const segments = path.split('/').filter(Boolean);

    if (segments.length > 0) {
      const maybeLang = segments[0] as Language;

      if (SUPPORTED_LANGUAGES.includes(maybeLang)) {
        return maybeLang;
      }
    }

    return null;
  };

  useEffect(() => {
    const pathLanguage = getLanguageFromPath();

    if (pathLanguage && SUPPORTED_LANGUAGES.includes(pathLanguage)) {
      setLanguage(pathLanguage);
      return;
    }

    const detectInitialLanguage = () => {
      if (language !== null) return;

      const resolved = i18n.resolvedLanguage as Language | undefined;
      const detected =
        resolved && SUPPORTED_LANGUAGES.includes(resolved)
          ? resolved
          : (i18n.language as Language) || FALLBACK_LANGUAGE;

      setLanguage(detected);
    };

    const onChange = (lng: Language) => setLanguage(lng);

    if (i18n.isInitialized) detectInitialLanguage();
    else i18n.on('initialized', detectInitialLanguage);

    i18n.on('languageChanged', onChange);
    return () => {
      i18n.off('initialized', detectInitialLanguage);
      i18n.off('languageChanged', onChange);
    };
  }, []);

  useEffect(
    () =>
      useSessionStore.subscribe(({ session }, { session: prevSession }) => {
        if (!session || session.accessToken === prevSession?.accessToken) {
          return;
        }

        const preferredLanguage =
          session.additional?.preferredLanguage?.toLowerCase() as
            | Language
            | undefined;

        if (
          preferredLanguage &&
          SUPPORTED_LANGUAGES.includes(preferredLanguage)
        ) {
          setLanguage(preferredLanguage);
        }
      }),
    []
  );

  useEffect(() => {
    if (language) {
      applyLanguage(language);
    }
  }, [language]);

  return null;
};

export { LanguageProvider };
